var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PlaceListCard from '../cards/PlaceListCard';
import { debounce, getDistanceInMiles, isEmpty, milesToMeters, } from '../../../utils';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
var containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    padding: '5px 5px 0px 5px',
};
var GooglePlaceSelector = function (_a) {
    var inputDisabled = _a.inputDisabled, searchRadius = _a.searchRadius, userLocation = _a.userLocation, onPlaceAdded = _a.onPlaceAdded, onPlaceSelected = _a.onPlaceSelected, onClear = _a.onClear;
    var map = useMap();
    var placesLib = useMapsLibrary('places');
    var inputRef = useRef(null);
    var serviceRef = useRef(null);
    var paginationRef = useRef(null);
    var getNextPageRef = useRef(null);
    var _b = useState({
        foundPlaces: [],
        // foundPlaces: MOCK_PLACES,
        searchQuery: '',
    }), componentState = _b[0], setComponentState = _b[1];
    var updateComponentState = function (newComponentState) {
        setComponentState(function (componentState) {
            return __assign(__assign({}, componentState), newComponentState);
        });
    };
    useEffect(function () {
        if (!placesLib || !map)
            return;
        serviceRef.current = new placesLib.PlacesService(map);
    }, [placesLib, map]);
    var loadMoreEnabled = function () {
        var _a;
        return ((((_a = paginationRef.current) === null || _a === void 0 ? void 0 : _a.hasNextPage) &&
            componentState.foundPlaces.length > 0) ||
            false);
    };
    var onLoadMore = function () {
        if (getNextPageRef.current) {
            getNextPageRef.current();
        }
    };
    var addPlaces = function (places) {
        // The setComponentState function is called with a function as its argument.
        // This function receives the previous state (prevState) and returns the new state.
        // This approach ensures that the state update is based on the most recent state,
        // which is crucial for correctly appending new places to the existing ones.
        setComponentState(function (prevState) {
            var parsedResults = places.map(function (result, index) {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                var placeDistance = getDistanceInMiles(userLocation, {
                    lat: ((_b = (_a = result.geometry) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.lat()) || 0,
                    lng: ((_d = (_c = result.geometry) === null || _c === void 0 ? void 0 : _c.location) === null || _d === void 0 ? void 0 : _d.lng()) || 0,
                });
                return {
                    id: result.place_id || "place-".concat(index),
                    placeId: result.place_id || '',
                    name: result.name,
                    address: result.formatted_address || '',
                    distance: placeDistance,
                    description: '',
                    photos: ((_e = result.photos) === null || _e === void 0 ? void 0 : _e.map(function (photo) { return photo.getUrl(); })) || [],
                    lat: ((_g = (_f = result.geometry) === null || _f === void 0 ? void 0 : _f.location) === null || _g === void 0 ? void 0 : _g.lat()) || 0,
                    lng: ((_j = (_h = result.geometry) === null || _h === void 0 ? void 0 : _h.location) === null || _j === void 0 ? void 0 : _j.lng()) || 0,
                };
            });
            return __assign(__assign({}, prevState), { foundPlaces: __spreadArray(__spreadArray([], prevState.foundPlaces, true), parsedResults, true) });
        });
    };
    var searchPlaces = function (searchQuery) {
        if (serviceRef.current && !isEmpty(searchQuery)) {
            var searchRadiusInMeters = milesToMeters(searchRadius);
            var request = {
                query: searchQuery,
                location: userLocation,
                radius: searchRadiusInMeters,
            };
            // https://developers.google.com/maps/documentation/javascript/places#place_search_requests
            // const request: google.maps.places.PlaceSearchRequest = {
            //   keyword: searchQuery,
            //   location: userLocation,
            //   radius: searchRadiusInMeters,
            //   rankBy: google.maps.places.RankBy.PROMINENCE,
            // };
            //serviceRef.current.nearbySearch(
            serviceRef.current.textSearch(request, function (results, status, pagination) {
                if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                    addPlaces(results);
                    if (pagination && pagination.hasNextPage) {
                        paginationRef.current = pagination;
                        getNextPageRef.current = function () {
                            pagination.nextPage();
                        };
                    }
                    else {
                        paginationRef.current = null;
                        getNextPageRef.current = null;
                    }
                }
            });
        }
    };
    // debouncedSearchPlaces Currently not used to save money on API calls
    var debouncedSearchPlaces = useCallback(debounce(function (query) {
        searchPlaces(query);
    }, 500), [searchPlaces]);
    // handleInputChange Currently not used to save money on API calls
    var handleInputChange = function (e) {
        var value = e.target.value;
        // If the input is empty, reset the search
        if (isEmpty(value)) {
            searchReset();
        }
        // Otherwise, search for places
        else {
            // If the input is at least 3 characters long, search for places
            if (value.length >= 3) {
                debouncedSearchPlaces(value);
            }
        }
        updateComponentState(__assign(__assign({}, componentState), { foundPlaces: [] }));
    };
    var handleKeyDown = function (e) {
        if (e.key === 'Enter') {
            handleInputChange(e);
        }
    };
    var searchReset = function () {
        if (inputRef.current)
            inputRef.current.value = ''; // Clear the input field
        paginationRef.current = null; // Reset the pagination reference
        getNextPageRef.current = null; // Reset the getNextPage reference
    };
    var handleClear = function () {
        searchReset();
        updateComponentState(__assign(__assign({}, componentState), { searchQuery: '', foundPlaces: [] }));
        onClear();
    };
    return (_jsxs(Box, { style: containerStyle, children: [_jsx(TextField, { inputRef: inputRef, required: true, id: 'text', disabled: inputDisabled, placeholder: 'Search Food, Drinks, Restaurants, Entertainment, etc. and press Enter', size: 'small', fullWidth: true, variant: 'outlined', 
                // onChange={handleInputChange}
                onKeyDown: handleKeyDown, slotProps: {
                    inputLabel: { shrink: false },
                    input: {
                        endAdornment: (_jsx(InputAdornment, { position: 'end', children: _jsx(IconButton, { onClick: handleClear, disabled: inputDisabled, sx: {
                                    '&:hover': {
                                        backgroundColor: 'transparent', // Remove hover background
                                    },
                                }, children: _jsx(FontAwesomeIcon, { size: 'sm', icon: faXmark }) }) })),
                    },
                }, sx: {
                    backgroundColor: 'white',
                    borderRadius: '4px',
                } }), _jsx(PlaceListCard, { places: componentState.foundPlaces, loadMoreEnabled: loadMoreEnabled(), onPlaceSelected: onPlaceSelected, onPlaceAdded: onPlaceAdded, onLoadMore: onLoadMore })] }));
};
export default GooglePlaceSelector;
